import type { TogglableNotificationCategory } from '@proref/types';
import { useNotificationPrefsQuery, useUpdateNotificationPrefs } from '../../hooks/use-notifications';
import { notifyError } from '../../lib/toast';
import { Toggle } from '../../components/shared/Toggle';
import { Skeleton } from '../../components/shared/Skeleton';
import { Button } from '../../components/shared/Button';
import { SettingsCard } from './SettingsCard';

interface CategoryCopy {
  key: TogglableNotificationCategory;
  label: string;
  description: string;
  // Only shown to the roles that can actually receive this category — offering a referee a switch
  // for transcode failures would be a control that does nothing.
  roles?: Array<'referee' | 'federation_admin' | 'super_admin'>;
}

// Six switches, not thirty. Per-event granularity was considered and dropped: nobody tunes twenty
// checkboxes, and a settings page people abandon halfway is worse than a coarse one they finish.
const CATEGORIES: CategoryCopy[] = [
  { key: 'daily', label: 'Daily clip & streak', description: "Today's clip is ready, and a warning before your streak runs out." },
  { key: 'exams', label: 'Exams', description: 'Results released, new exams published, and changes to your sittings.' },
  { key: 'webinars', label: 'Webinars', description: 'Reservations, join links, reminders and schedule changes.' },
  { key: 'discussion', label: 'Discussion', description: 'Replies to your comments, and upvotes (batched, never one per vote).' },
  {
    key: 'admin',
    label: 'Federation admin',
    description: 'Clip processing, invites redeemed, flagged sittings and seats on your webinars.',
    roles: ['federation_admin', 'super_admin'],
  },
  {
    key: 'platform',
    label: 'Platform',
    description: 'Cost thresholds and platform-wide events.',
    roles: ['super_admin'],
  },
];

interface NotificationSettingsProps {
  // Passed rather than read from context so the tab stays a pure function of its inputs and the
  // role-filtering is trivial to test.
  role: 'referee' | 'federation_admin' | 'super_admin' | null;
}

export function NotificationSettings({ role }: NotificationSettingsProps) {
  const { data: prefs, isLoading, isError, refetch } = useNotificationPrefsQuery();
  const update = useUpdateNotificationPrefs();

  const visible = CATEGORIES.filter((c) => !c.roles || (role && c.roles.includes(role)));

  return (
    <>
      <SettingsCard
        title="In-app notifications"
        description="What shows up in the bell. Turning a category off stops new notifications in it — it does not remove ones you already have."
      >
        {isLoading ? (
          <div className="space-y-3">
            {visible.map((c) => (
              <Skeleton key={c.key} className="h-9 w-full" />
            ))}
          </div>
        ) : isError || !prefs ? (
          // Fetch failure renders in place of the data, never as a toast (CLAUDE.md §4).
          <div className="py-6 text-center">
            <p className="text-[13px] text-muted">Could not load your notification settings.</p>
            <Button size="sm" className="mt-3" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-line">
            {visible.map((category) => (
              <div key={category.key} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-ink">{category.label}</p>
                  <p className="mt-0.5 text-[12px] leading-relaxed text-muted">{category.description}</p>
                </div>
                <div className="mt-0.5 flex-none">
                  <Toggle
                    checked={prefs[category.key].in_app}
                    label={category.label}
                    onChange={(value) => {
                      // Send ONLY the category that changed — the API merges per channel, and
                      // sending the resolved set would freeze today's defaults into this user's
                      // row so untouched categories stopped tracking them.
                      update.mutate(
                        { [category.key]: { in_app: value } },
                        // No success toast: the switch moving IS the confirmation, and a toast
                        // landing after the round-trip reads as lag on a control that already
                        // moved. The error toast fires on rollback only.
                        { onError: (err) => notifyError(err, 'Could not save that setting') },
                      );
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>

      <SettingsCard
        title="Always on"
        description="Some notifications ignore these switches, because not sending them would leave you acting on something untrue — a role you no longer have, a seat at a session that was cancelled, or a comment you think is still standing."
      >
        <ul className="space-y-1.5 text-[13px] text-muted">
          <li>Changes to your role, federation or password</li>
          <li>A webinar you reserved being cancelled</li>
          <li>Something of yours removed by a moderator</li>
        </ul>
      </SettingsCard>

      <SettingsCard
        title="Email"
        description="ProRef sends no notification emails yet — only your invite and password-reset messages, which are not notifications. When email arrives, these same categories will gain a second switch."
      >
        <p className="text-[13px] text-muted-2">Nothing to configure yet.</p>
      </SettingsCard>
    </>
  );
}
